import type { CelestialData } from '@/services/celestial-service';
import type { WeatherConditionsDisplay } from '@/services/weather-service';
import type { VariableScore, QualityLevel, ConditionsQualityResult } from '@/types/conditions';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BASE_WEIGHTS = {
  temperature: 0.40,
  pressure: 0.20,
  wind: 0.15,
  crepuscular: 0.25,
} as const;

const DISCLAIMER =
  'Hunt rating based on weather conditions — individual property and deer patterns vary';

const LEVEL_DESCRIPTIONS: Record<QualityLevel, string> = {
  excellent: 'Conditions are highly favorable for hunting',
  good: 'Conditions are favorable for hunting',
  fair: 'Conditions are moderate',
  poor: 'Conditions are unfavorable',
};

const LEVEL_LABELS: Record<QualityLevel, string> = {
  excellent: 'PRIME',
  good: 'GOOD',
  fair: 'FAIR',
  poor: 'POOR',
};

// ---------------------------------------------------------------------------
// Per-variable scorers (pure functions, exported for testing)
// ---------------------------------------------------------------------------

/**
 * Score temperature based on how far current temp falls below the seasonal average.
 * Returns null if seasonalAvg is not available — caller redistributes weight.
 */
export function scoreTemperature(
  currentTemp: number,
  dailyHigh: number | null,
  seasonalAvg: number | null,
): VariableScore | null {
  if (seasonalAvg === null) return null;

  const delta = seasonalAvg - currentTemp; // positive = current is below avg (favorable)
  const roundedTemp = Math.round(currentTemp);
  const absDelta = Math.round(Math.abs(delta));

  let score: number;
  let label: string;

  if (delta >= 10) {
    score = 100;
    label = `${absDelta}°F below seasonal average`;
  } else if (delta >= 5) {
    score = 70;
    label = `${absDelta}°F below seasonal average`;
  } else if (delta > -5) {
    score = 40;
    if (Math.abs(delta) < 1) {
      label = 'Near seasonal average';
    } else if (delta > 0) {
      label = `${absDelta}°F below seasonal average`;
    } else {
      label = `${absDelta}°F above seasonal average`;
    }
  } else {
    score = 10;
    label = `${absDelta}°F above seasonal average`;
  }

  const rawParts: string[] = [`${roundedTemp}°F`];
  if (dailyHigh !== null) {
    rawParts.push(`high ${Math.round(dailyHigh)}°F`);
  }

  return {
    variable: 'temperature',
    score,
    weight: BASE_WEIGHTS.temperature,
    label,
    raw: rawParts.join(' · '),
  };
}

/**
 * Score barometric pressure based on value and trend.
 * Sweet spot: 29.90–30.30 inHg (ADR-017).
 */
export function scorePressure(
  pressureInHg: number,
  trend: 'rising' | 'falling' | 'steady',
): VariableScore {
  const inSweetSpot = pressureInHg >= 29.9 && pressureInHg <= 30.3;

  let score: number;
  let label: string;

  if (inSweetSpot && trend === 'rising') {
    score = 100;
    label = `${pressureInHg.toFixed(2)} inHg, rising`;
  } else if (inSweetSpot && trend === 'steady') {
    score = 70;
    label = `${pressureInHg.toFixed(2)} inHg, steady`;
  } else if (inSweetSpot && trend === 'falling') {
    score = 50;
    label = `${pressureInHg.toFixed(2)} inHg, falling`;
  } else {
    score = 20;
    const trendLabel = trend === 'rising' ? ', rising' : trend === 'falling' ? ', falling' : '';
    label = `${pressureInHg.toFixed(2)} inHg${trendLabel} (outside sweet spot)`;
  }

  return {
    variable: 'pressure',
    score,
    weight: BASE_WEIGHTS.pressure,
    label,
    raw: `${pressureInHg.toFixed(2)} inHg`,
  };
}

/**
 * Score wind speed. Moderate wind aids scent dispersal; calm or very high wind is less ideal.
 */
export function scoreWind(speedMph: number, direction?: string): VariableScore {
  let score: number;
  let label: string;

  if (speedMph >= 5 && speedMph <= 15) {
    score = 80;
    label = 'Moderate — good scent dispersal';
  } else if (speedMph < 5) {
    score = 60;
    label = 'Calm — minimal scent dispersal';
  } else if (speedMph <= 25) {
    score = 50;
    label = 'Breezy — scent dispersal variable';
  } else {
    score = 30;
    label = 'High wind — movement likely reduced';
  }

  const rawSpeed = `${Math.round(speedMph)} mph`;
  const raw = direction ? `${direction} ${rawSpeed}` : rawSpeed;

  return {
    variable: 'wind',
    score,
    weight: BASE_WEIGHTS.wind,
    label,
    raw,
  };
}

/**
 * Score proximity to crepuscular windows (civil dawn / civil dusk).
 * The crepuscular window is the most reliable activity signal (ADR-017).
 */
export function scoreCrepuscular(now: Date, civilDawn: Date, civilDusk: Date): VariableScore {
  const nowMs = now.getTime();
  const dawnMs = civilDawn.getTime();
  const duskMs = civilDusk.getTime();

  const minsFromDawn = Math.abs(nowMs - dawnMs) / (60 * 1000);
  const minsFromDusk = Math.abs(nowMs - duskMs) / (60 * 1000);
  const closestMins = Math.min(minsFromDawn, minsFromDusk);

  const isNearDawn = minsFromDawn <= minsFromDusk;
  const windowName = isNearDawn ? 'dawn' : 'dusk';

  let score: number;
  let label: string;

  if (closestMins <= 30) {
    score = 100;
    label = `Within 30 min of ${windowName}`;
  } else if (closestMins <= 60) {
    score = 85;
    const mins = Math.round(closestMins);
    label = `${mins} min from ${windowName}`;
  } else if (closestMins <= 120) {
    score = 60;
    const hrs = (closestMins / 60).toFixed(1);
    label = `${hrs} hr from ${windowName}`;
  } else {
    score = 20;
    label = 'Midday — low crepuscular activity expected';
  }

  const roundedMins = Math.round(closestMins);
  const raw = closestMins <= 90
    ? `${roundedMins} min to ${windowName}`
    : `${(closestMins / 60).toFixed(1)} hr to ${windowName}`;

  return {
    variable: 'crepuscular',
    score,
    weight: BASE_WEIGHTS.crepuscular,
    label,
    raw,
  };
}

// ---------------------------------------------------------------------------
// Composite computation
// ---------------------------------------------------------------------------

/**
 * Compute the weighted composite score from an array of variable scores.
 * The weights in the VariableScore objects must already sum to 1.0.
 */
export function computeWeightedScore(scores: VariableScore[]): { total: number; level: QualityLevel } {
  const total = Math.round(
    scores.reduce((sum, v) => sum + v.score * v.weight, 0),
  );

  let level: QualityLevel;
  if (total >= 80) level = 'excellent';
  else if (total >= 60) level = 'good';
  else if (total >= 40) level = 'fair';
  else level = 'poor';

  return { total, level };
}

/**
 * Main entry point. Synthesizes weather and celestial data into a quality score.
 * Returns null when weather data is unavailable — celestial-only data is not sufficient.
 *
 * The `now` parameter defaults to the current time but is injectable for deterministic testing.
 * The `seasonalAvgTemp` parameter is nullable: when absent, the temperature weight is
 * redistributed proportionally to the remaining three variables.
 */
export function computeConditionsQuality(
  weather: WeatherConditionsDisplay | null,
  celestial: CelestialData,
  now: Date = new Date(),
  seasonalAvgTemp: number | null = null,
): ConditionsQualityResult | null {
  if (weather === null) return null;

  const tempScore = scoreTemperature(weather.temp, weather.tempToday, seasonalAvgTemp);
  const pressureScore = scorePressure(weather.pressure, weather.pressureTrend ?? 'steady');
  const windScore = scoreWind(weather.windSpeed, weather.windDirection);
  const crepScore = scoreCrepuscular(now, celestial.sun.civilDawn, celestial.sun.civilDusk);

  let variables: VariableScore[];

  if (tempScore !== null) {
    variables = [tempScore, pressureScore, windScore, crepScore];
  } else {
    // Redistribute temperature weight (0.40) proportionally across remaining variables.
    // Remaining base weights: pressure 0.20, wind 0.15, crepuscular 0.25 → sum 0.60
    const remainingSum = BASE_WEIGHTS.pressure + BASE_WEIGHTS.wind + BASE_WEIGHTS.crepuscular;
    variables = [
      { ...pressureScore, weight: BASE_WEIGHTS.pressure / remainingSum },
      { ...windScore, weight: BASE_WEIGHTS.wind / remainingSum },
      { ...crepScore, weight: BASE_WEIGHTS.crepuscular / remainingSum },
    ];
  }

  const { total, level } = computeWeightedScore(variables);

  return {
    score: total,
    level,
    label: LEVEL_LABELS[level],
    description: LEVEL_DESCRIPTIONS[level],
    variables,
    computedAt: now,
    disclaimer: DISCLAIMER,
  };
}
